import { useCallback, useEffect, useState } from 'react';
import { verticalLifecycleApi, type OperatorRoster } from '@/api/verticalLifecycle';

// Founder-handover cockpit (CTO-MASTER-OPERATOR-HANDOVER-1). The CTO master /
// Director grants a vertical's operator seat to a founder's own account and can
// revoke it; the founder then runs THIS vertical (customers/pricing/costs)
// without the CTO account. Grant/revoke are super-operator only — the backend
// re-enforces (403 requires_super_operator) regardless of what this UI shows, so
// this tab is rendered only for a confirmed super-operator.
//
// INTERIM (flagged): this is a runtime operator allow-list over the vertical
// registry — the "founder takes the operator seat" (running) half. The founder-
// OWNER commercial layer (IP + revenue share + graduation entity) is a separate,
// later item and is deliberately NOT modeled here.

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(error.message) as { detail?: unknown; message?: unknown };
    const detail = parsed.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const m = (detail as { message?: unknown }).message;
      if (typeof m === 'string') return m;
    }
    if (typeof parsed.message === 'string') return parsed.message;
  } catch {
    /* ApiError.message may be a plain body string */
  }
  return error.message;
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().replace('T', ' ').slice(0, 16);
}

export function OperatorsTab({ vertical }: { vertical: string }) {
  const [roster, setRoster] = useState<OperatorRoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRoster(await verticalLifecycleApi.listOperators(vertical));
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [vertical]);

  useEffect(() => {
    void load();
  }, [load]);

  const onGrant = useCallback(async () => {
    const email = emailInput.trim();
    if (!email) return;
    setActionLoading('grant');
    setActionError(null);
    setActionMessage(null);
    try {
      const result = await verticalLifecycleApi.grantOperator(vertical, email);
      setRoster(result);
      setEmailInput('');
      const noun = result.status === 'granted' ? 'granted' : result.status.replace('_', ' ');
      setActionMessage(`${email} — ${noun} on ${vertical}.`);
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setActionLoading(null);
    }
  }, [emailInput, vertical]);

  const onRevoke = useCallback(
    async (email: string) => {
      setActionLoading(`revoke:${email}`);
      setActionError(null);
      setActionMessage(null);
      try {
        const result = await verticalLifecycleApi.revokeOperator(vertical, email);
        setRoster(result);
        setActionMessage(`${email} — revoked on ${vertical}.`);
      } catch (error) {
        setActionError(errorMessage(error));
      } finally {
        setActionLoading(null);
      }
    },
    [vertical],
  );

  return (
    <div className="px-6 pb-6 pt-4 max-w-2xl">
      <div className="mb-4">
        <h3 className="text-sm text-gray-200">Operator seats — {vertical}</h3>
        <p className="text-[11px] text-gray-500 mt-1">
          Grant a founder the operator seat of this vertical so they can run it (customers,
          pricing, compute &amp; costs) on their own account. You keep harbor-wide oversight.
          Bootstrap (registry) seats can’t be revoked here.
        </p>
      </div>

      {actionMessage && (
        <div role="status" className="mb-3 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs rounded p-2">
          {actionMessage}
        </div>
      )}
      {actionError && (
        <div role="alert" className="mb-3 border border-red-500/40 bg-red-500/5 text-red-300 text-xs rounded p-2">
          {actionError}
        </div>
      )}

      <div className="flex items-center gap-2 mb-5">
        <input
          type="email"
          aria-label="Founder email"
          placeholder="founder@their-domain.com"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void onGrant();
          }}
          className="flex-1 bg-black/40 border border-gray-800 rounded px-2 py-1.5 text-xs text-gray-200"
        />
        <button
          type="button"
          disabled={actionLoading !== null || !emailInput.trim()}
          onClick={() => void onGrant()}
          className="px-3 py-1.5 text-xs rounded border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-40"
        >
          {actionLoading === 'grant' ? 'Granting…' : 'Grant seat'}
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-gray-500">Loading operators…</div>
      ) : loadError ? (
        <div role="alert" className="text-xs text-red-300">{loadError}</div>
      ) : roster ? (
        <div className="space-y-4">
          <section>
            <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-2">
              Granted founders ({roster.granted_operators.length})
            </div>
            {roster.granted_operators.length === 0 ? (
              <div className="text-xs text-gray-600">No founder seats granted yet.</div>
            ) : (
              <ul className="space-y-1">
                {roster.granted_operators.map((op) => (
                  <li
                    key={op.email}
                    className="flex items-center justify-between border border-gray-800 rounded px-3 py-2"
                  >
                    <div>
                      <div className="text-xs text-gray-200">{op.email}</div>
                      <div className="text-[10px] text-gray-600">
                        {op.granted_by ? `granted by ${op.granted_by}` : 'granted'}
                        {formatDateTime(op.granted_at) ? ` · ${formatDateTime(op.granted_at)}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={actionLoading !== null}
                      onClick={() => void onRevoke(op.email)}
                      className="px-2 py-1 text-[11px] rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-40"
                    >
                      {actionLoading === `revoke:${op.email}` ? 'Revoking…' : 'Revoke'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-wider text-gray-600 mb-2">
              Bootstrap seats (registry — not revocable here)
            </div>
            {roster.static_operators.length === 0 ? (
              <div className="text-xs text-gray-600">None.</div>
            ) : (
              <ul className="space-y-1">
                {roster.static_operators.map((op) => (
                  <li key={op.email} className="text-xs text-gray-400 border border-gray-900 rounded px-3 py-2">
                    {op.email} <span className="text-gray-700">· registry</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
